
import { useCallback, useMemo, useState } from 'react';
import JobDetails from './JobDetails';
import { Allotment } from 'allotment';
import useNetwork from '../common/hooks/networkHook';
import { searchJobs } from '../reducers/jobSlice';
import useClientConfig from '../common/hooks/clientConfigHook';
import { useType } from '../reducers/typeSlice';
import { useSite } from '../reducers/siteSlice';
import { useLevel } from '../reducers/levelSlice';
import { useProblem } from '../reducers/problemSlice';
import { priorityIcon, usePriorities } from '../reducers/prioritySlice';
import { useTranslation } from 'react-i18next';
import { Col, Row } from '../common/components/Grid';
import TextInput from '../common/components/TextInput';
import Select from '../common/components/Select';
import DateInput from '../common/components/DateInput';
import TimeInput from '../common/components/TimeInput';
import Checkbox from '../common/components/Checkbox';
import PropTypes from 'prop-types';
import DataTable from '../common/components/DataTable';
import { useJobTags } from '../reducers/jobTagSlice';
import { ReactNodeDialog, TextPromptDialog } from '../common/utils/dialogUtlities';
import { ProgressBar } from 'react-bootstrap';
import { printJobs } from '../utils/bulkJobPrinting';
import useCredentials from "../common/hooks/credentialsHook";
import Icon from '../common/components/Icon';
import { cdate, now } from '../common/utils/CDate';
import { useBroadcast } from '../common/hooks/broadcastHook';
import DefaultButton from '../common/components/DefaultButton';
import PanelTitle from '../common/components/PanelTitle';
import { useCADPopout } from '../utils/CADStateProvider';
import { useRealizationContext } from '../common/reducers/utilities';
import { deepMerge } from '../common/utils/objectUtilities';
import useMessageDisplay from '../common/components/MessageDisplay';
import { buildShadedColor } from '../common/utils/tableUtilities';

export const JobDisplayTable = ({ job, jobs: jobsInput, onSelect, csvFilename, customExporters }) => {
    const priorities = usePriorities();
    const priorityMap = useMemo(() => {
        const priorityMap = {};
        priorities?.forEach(p => priorityMap[p.internalid] = { icon: priorityIcon(p), ...buildShadedColor(p.color), text: p.text, value: p.value });
        return priorityMap;
    }, [priorities]);
    const jobs = useMemo(() => jobsInput?.map(j => {
        return ({ ...j, priorityValue: priorityMap[j.priority]?.value })
    }), [jobsInput, priorityMap])
    const clientConfig = useClientConfig();
    const { t } = useTranslation();
    const jobTags = useJobTags();


    const site = useSite(job?.site);
    const level = useLevel(job?.site, job?.level);
    const type = useType(job?.site, job?.level, job?.type);
    const problem = useProblem(job?.site, job?.level, job?.type, job?.problem);

    const rowClick = job => {
        if (onSelect) {
            onSelect(job);
        }
    }

    let getRowStyle = (job, disabled) => ({ backgroundColor: priorityMap[job.priority]?.backgroundColor, color: priorityMap[job.priority]?.color, cursor: 'pointer' })

    const columns = useMemo(() => {
        let columns = [
            { type: 'dynamic', key: 'priority', field: 'priority', sortfield: 'priorityValue', title: t('priority_one'), def: (value, disabled, hideLabel, onChange) => <Icon literal={priorityMap[value.priority]?.icon}></Icon>, toString: (value) => priorityMap[value.priority]?.text ?? t('unknown') },
            { type: 'text', key: 'displayid', field: 'displayid', title: t('jobNumber') },
            { type: 'text', field: 'origin', title: t('origin') },
            { type: 'text', field: 'status', title: t('status_one') },
            { type: 'datetime', field: 'created', title: t('created') },
            { type: 'datetime', field: 'lastupdate', title: t('lastUpdated') },
            { type: 'text', field: 'location', title: t('location_one') }
        ];
        if (clientConfig.enableSites) {
            columns.push({ type: 'text', field: 'site', title: t('site_one') });
        }
        if (clientConfig.enableLevels) {
            columns.push({ type: 'text', field: 'level', title: t('level_one') });
        }
        columns = columns.concat([{ type: 'text', field: 'type', title: t('type_one') },
        { type: 'text', field: 'problem', title: t('problem_one') }])
        return columns;
    }, [clientConfig.enableSites, clientConfig.enableLevels, priorityMap, t]);

    const extraColumns = useMemo(() => {
        let columns = [
            { type: 'text', field: 'gender', title: t('gender') },
            { type: 'number', field: 'age', title: t('age') },
            { type: 'text', field: 'gridx', title: t('gridX') },
            { type: 'text', field: 'gridy', title: t('gridY') },
            { type: 'number', field: 'latitude', title: t('latitude') },
            { type: 'number', field: 'longitude', title: t('longitude') },
            { type: 'text', field: 'creator', title: t('creator') },
            { type: 'text', field: 'result', title: t('result_one') },
            { type: 'datetime', field: 'closed', title: t('closed') },
            { type: 'datetime', field: 'onscene', title: t('onScene') },
            { type: 'number', field: 'duration', title: t('duration') },
            { type: 'text', field: 'w3w', title: clientConfig.altcoordLbl },
            //{ type: 'text', field: 'origindetails', title: t('origindetails')},// TODO add translation
            { type: 'tag', field: 'tags', title: t('tag_other'), key: 'tag', value: 'id', values: jobTags }
        ]
        // custom fields
        const excludedTypes = ['label', 'object', 'iframe', 'button', 'signature', 'image', 'file'];
        const customfieldFilter = (def) => !excludedTypes.includes(def.type) && def.label && def.field;
        if (clientConfig.jobGeneralFormSpec) {
            let formSpec = clientConfig.jobGeneralFormSpec;
            let defs = formSpec?.flatMap(section => section.rows?.flatMap(row => row.filter(cell => customfieldFilter(cell)).map(cell => ({ ...cell, field: 'generalcustomfields.' + cell.field, title: cell.label }))) ?? []) ?? [];
            columns = columns.concat(defs);
        }
        [site, level, type, problem].forEach(def => {
            if (def?.formdef) {
                let formSpec = def.formdef;
                let defs = formSpec?.flatMap(section => section.rows?.flatMap(row => row.filter(cell => customfieldFilter(cell)).map(cell => ({ ...cell, field: 'customfields.' + cell.field, title: cell.label }))) ?? []) ?? [];
                columns = columns.concat(defs);
            }
        })
        return columns
    }, [site, level, type, problem, t, clientConfig.jobGeneralFormSpec, clientConfig.altcoordLbl, jobTags]);


    return <DataTable columns={columns} additionalColumns={extraColumns} customExporters={customExporters} value={jobs} readonly={true} onRowClick={rowClick} getRowStyle={getRowStyle} emptyMessage={t('noJobsFound')} defaultSort={['displayid']} csvFilename={csvFilename}></DataTable>
}

const JobSearch = ({ exclude, filter, onSelect }) => {
    const { t, i18n } = useTranslation();
    const realizationContext = useRealizationContext();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const priorities = usePriorities();
    const [job, setJob] = useState({});
    const [results, setResults] = useState([]);
    const [printPending, setPrintPending] = useState(false);
    const [printingTemplate, setPrintingTemplate] = useState();
    const [progress, setProgress] = useState();
    const credentials = useCredentials();
    const { showError } = useMessageDisplay();

     const handleUpdate = useCallback(delta => {
        setJob(job => {
            const newJob = deepMerge({}, job, delta );
            Object.keys(newJob).forEach(f => {
                if (newJob[f] === null || newJob[f] === undefined) {
                    delete newJob[f];
                }
            });
            return newJob;
        });
    }, []);
   
    // TODO this useEffect needs to depend on excludes but it creates an infinite cycle with related jobs some how...
    const runSearch = useCallback(() => {
        let superceeded = false;
        (async () => {
            if (Object.keys(job).length > 0) {
                let jobs = await (searchJobs(job)(network));
                if (!superceeded) {
                    if (jobs && Array.isArray(jobs)) {
                        setResults(jobs.filter(j => (!filter || filter(j)) && (!exclude || exclude.every(e => e.toString() !== j.jobid.toString()))));
                    } else {
                        setResults([]);
                    }
                }
            } else {
                setResults([]);
            }
        })();
        return () => superceeded = true;
    }, [exclude, filter, job, network]);

    const printAll = useCallback((reason) => {
        if (!reason) {
            setPrintPending(false);
            return;
        }
        let note = t('jobPrintNote', { username: credentials.username, now: now().dateTimeWithSecondsFormat(clientConfig.tz.enable), reason })
        printJobs(setProgress, realizationContext, i18n.resolvedLanguage, results.map(job => ({
            ...job,
            priority: priorities.find(p => p.internalid === job.priority)?.text ?? job.priority,
            createdHour: cdate(job.created).hours(),
            duration: job.created && job.closed ? cdate(job.closed, clientConfig.tz).diff(cdate(job.created, clientConfig.tz), 'hour', true) : null,
            closedHour: job.closed ? cdate(job.closed).hours() : null
        })), note, credentials.username, job.start, job.end, {
            templateDoc: printingTemplate,
            context: job,
            onError: error => showError(t('downloadFailed', { details: error?.message ?? t('unknown') }))
        });
        setPrintPending(false);
    }, [credentials, realizationContext, priorities, clientConfig, printingTemplate, job, showError, i18n.resolvedLanguage, results, t]);

    const clear = useCallback(() => {
        setJob({});
        setResults([]);
    }, []);

    const customExport = useMemo(() => {
        const reportTemplates = (clientConfig.reportTemplate || []).map(template => ({label: template.name, export:() => { setPrintPending(true); setPrintingTemplate(template); }}))
        return [{label: t('printAll'), export: () => setPrintPending(true)}].concat(reportTemplates);
    }, [clientConfig.reportTemplate, t]);
    
    const originOptions = useMemo(() => ["Chronicler", "Chronolink", "Scheduled", t('voice'), t('sms')], [t]);    

    return <div className="h-100" style={{ flex:1 }}>
        <ReactNodeDialog showModal={progress !== undefined} title={t('generating')}>
            <ProgressBar now={progress} label={`${progress}%`}></ProgressBar>
        </ReactNodeDialog>
        <TextPromptDialog title={t('confirmPrinting')} message={t('jobPrintReason')} maxLength={256} showModal={!!printPending} placeholder={t('reason')} onClose={printAll}></TextPromptDialog>

        <Allotment snap={true} vertical={true}>
            <Allotment.Pane>
                <div className="d-flex flex-column h-100">
                    <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                        <Row>
                            <Col md={2}>
                                <TextInput placeholder={t('jobNumber')} live={true} value={job.displayid} onChange={value => handleUpdate({ displayid: value })}></TextInput>
                            </Col>
                            <Col md={2}>
                                <Select placeholder={t('origin')} value={job.origin} onChange={value => handleUpdate({ origin: value })} options={originOptions}></Select>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <JobDetails job={job} isSearch={true} onChange={handleUpdate}></JobDetails>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="2">
                                <TextInput placeholder={t('creator')} live={true} value={job.creator} onChange={value => handleUpdate({ creator: value })}></TextInput>
                            </Col>
                            <Col md="2">
                                <DateInput placeholder={t('fromDate')} value={job.start} onChange={value => handleUpdate({ start: value })}></DateInput>
                            </Col>
                            <Col md="2">
                                <TimeInput placeholder={t('fromTime')} value={job.start} onChange={value => handleUpdate({ start: value })}></TimeInput>
                            </Col>
                            <Col md="2">
                                <DateInput placeholder={t('toDate')} value={job.end} onChange={value => handleUpdate({ end: value })}></DateInput>
                            </Col>
                            <Col md="2">
                                <TimeInput placeholder={t('toTime')} value={job.end} onChange={value => handleUpdate({ end: value })}></TimeInput>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="2">
                                <TextInput placeholder={t('callsign')} live={true} value={job.callsign} onChange={value => handleUpdate({ callsign: value })}></TextInput>
                            </Col>
                            <Col md="6">
                                <TextInput placeholder={t('notes')} live={true} value={job.notes} onChange={value => handleUpdate({ notes: value })}></TextInput>
                            </Col>
                            <Col md>
                                <Checkbox placeholder={t('image_other')} checked={!!job.hasPictures} onChange={value => handleUpdate({ hasPictures: value })}></Checkbox>
                            </Col>
                            <Col md>
                                <Checkbox placeholder={t('attachement_other')} checked={!!job.hasAttachments} onChange={value => handleUpdate({ hasAttachments: value })}></Checkbox>
                            </Col>
                            <Col md>
                                <Checkbox placeholder={t('form_other')} checked={!!job.hasCustomForms} onChange={value => handleUpdate({ hasCustomForms: value })}></Checkbox>
                            </Col>
                        </Row>
                    </div>
                    <div className="py-2">
                        <Row>
                            <Col xs="auto">
                                <DefaultButton kind="clear" variant="outline-danger" onClick={clear}></DefaultButton>
                            </Col>
                            <Col></Col>
                            <Col xs="auto">
                                <DefaultButton kind="search" variant="outline-primary" onClick={runSearch}></DefaultButton>
                            </Col>
                        </Row>
                    </div>
                </div>
            </Allotment.Pane>
            <Allotment.Pane>
                <div className="pt-3" style={{ height: '100%' }}>
                    <JobDisplayTable job={job} jobs={results} onSelect={onSelect} customExporters={customExport} csvFilename={t('jobSearchResultsFilename')  + '.csv'}></JobDisplayTable>
                </div>
            </Allotment.Pane>
        </Allotment>
    </div>
}
JobSearch.propTypes = {
    onSelect: PropTypes.func.isRequired
}
export default JobSearch;

export const JobSearchPanel = () => {
    const { t } = useTranslation();
    const broadcast = useBroadcast();
    const popout = useCADPopout();
    return <div className="w-100 h-100 d-flex flex-column" id="jobSearchPanel">
        <PanelTitle title={t('jobSearch')} guide={81510} onPopout={() => popout('jobSearchPanel')}></PanelTitle>
        <JobSearch onSelect={job => broadcast('selectJob', { jobid: job.jobid })}></JobSearch>
    </div>
}